import { useEffect, useRef, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Image as ImageIcon, Mail, Save, X, Loader2, UsersRound, Copy, RefreshCw, UserMinus, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function SettingsPage() {
  const { user } = useAuth();
  const [branding, setBranding] = useState({ clinic_name: "", clinic_logo_data_url: "" });
  const [schedule, setSchedule] = useState({ enabled: false, day_of_week: 0, hour: 8 });
  const [saving, setSaving] = useState({ branding: false, schedule: false, roster: false });
  const [loading, setLoading] = useState(true);
  const [inviteCode, setInviteCode] = useState(null);
  const [members, setMembers] = useState([]);
  const logoRef = useRef(null);

  useEffect(() => {
    Promise.all([
      http.get("/team/branding").then((r) => setBranding(r.data)),
      http.get("/digest/schedule").then((r) => setSchedule(r.data)),
      http.get("/team/current-code").then((r) => setInviteCode(r.data.code)).catch(() => {}),
      http.get("/team/members").then((r) => setMembers(r.data)).catch(() => {}),
    ]).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot mount fetch; all setters + http are stable references.
  }, []);

  const saveBranding = async () => {
    setSaving((s) => ({ ...s, branding: true }));
    try {
      await http.patch("/team/branding", branding);
      toast.success("Branding saved — new PDFs will use it.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving((s) => ({ ...s, branding: false }));
    }
  };

  const saveSchedule = async () => {
    setSaving((s) => ({ ...s, schedule: true }));
    try {
      await http.patch("/digest/schedule", schedule);
      toast.success(schedule.enabled
        ? `Automatic digest set for ${DAYS[schedule.day_of_week]} ${schedule.hour.toString().padStart(2, "0")}:00 UTC`
        : "Automatic digest disabled");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving((s) => ({ ...s, schedule: false }));
    }
  };

  const uploadLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 600 * 1024) { toast.error("Please pick a logo under 600 KB."); return; }
    const reader = new FileReader();
    reader.onload = () => setBranding((b) => ({ ...b, clinic_logo_data_url: reader.result }));
    reader.readAsDataURL(f);
  };

  const rotateCode = async () => {
    setSaving((s) => ({ ...s, roster: true }));
    try {
      const r = await http.post("/team/invite-code");
      setInviteCode(r.data.code);
      toast.success("New invite code generated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving((s) => ({ ...s, roster: false }));
    }
  };

  const copyCode = () => {
    if (!inviteCode) return;
    navigator.clipboard?.writeText(inviteCode);
    toast.success("Copied to clipboard");
  };

  const changeRole = async (memberId, role) => {
    try {
      await http.post(`/team/members/${memberId}/role`, { role });
      setMembers((prev) => prev.map((m) => m.id === memberId ? { ...m, team_role: role, role_effective: role } : m));
      toast.success("Role updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const removeMember = async (memberId) => {
    if (!window.confirm("Remove this teammate? They'll return to their own solo workspace.")) return;
    try {
      await http.delete(`/team/members/${memberId}`);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      toast.success("Removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  if (loading) {
    return <div className="p-12 text-slate-500">Loading settings…</div>;
  }

  return (
    <div className="p-4 sm:p-8 md:p-12 max-w-3xl space-y-8">
      <PageHeader
        eyebrow="Settings"
        title="Clinic settings"
        description="Brand your exports and control who gets weekly progress emails."
      />

      {/* Branding */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="branding-section">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-4 h-4 text-slate-500" />
          <div className="font-display font-bold text-lg">Clinic branding</div>
        </div>
        <p className="text-sm text-slate-500 mb-5">Shown at the top of every generated PDF (program sheets, cut sheets, consent forms).</p>

        <div className="space-y-4">
          <div>
            <Label htmlFor="clinic_name">Clinic name</Label>
            <Input
              id="clinic_name"
              value={branding.clinic_name || ""}
              onChange={(e) => setBranding({ ...branding, clinic_name: e.target.value })}
              maxLength={80}
              placeholder="e.g. Bright Path ABA"
              className="mt-1.5 rounded-xl"
              data-testid="clinic-name-input"
            />
          </div>
          <div>
            <Label>Logo</Label>
            <div className="mt-1.5 flex items-center gap-4">
              <div className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                {branding.clinic_logo_data_url ? (
                  <img src={branding.clinic_logo_data_url} alt="Logo preview" className="w-full h-full object-contain" data-testid="clinic-logo-preview" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-slate-400" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={logoRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="clinic-logo-input" />
                <Button type="button" variant="outline" onClick={() => logoRef.current?.click()} className="rounded-full h-9 text-xs">
                  {branding.clinic_logo_data_url ? "Replace logo" : "Upload logo"}
                </Button>
                {branding.clinic_logo_data_url && (
                  <Button type="button" variant="ghost" onClick={() => setBranding((b) => ({ ...b, clinic_logo_data_url: "" }))} className="rounded-full h-9 text-xs text-rose-600">
                    <X className="w-3.5 h-3.5 mr-1" /> Remove
                  </Button>
                )}
              </div>
            </div>
            <div className="text-[11px] text-slate-500 mt-2">PNG or JPG, square works best · under 600 KB.</div>
          </div>
        </div>
        <Button onClick={saveBranding} disabled={saving.branding} className="rounded-full mt-6" data-testid="save-branding-button">
          {saving.branding ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save branding</>}
        </Button>
      </section>

      {/* Digest schedule */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="digest-section">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-4 h-4 text-slate-500" />
          <div className="font-display font-bold text-lg">Automatic weekly digest</div>
        </div>
        <p className="text-sm text-slate-500 mb-5">Get an email with your team's progress every week, without lifting a finger.</p>

        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-sm font-semibold">Enable auto-digest</div>
            <div className="text-xs text-slate-500">Sends only if there's activity that week.</div>
          </div>
          <Switch
            checked={schedule.enabled}
            onCheckedChange={(v) => setSchedule({ ...schedule, enabled: v })}
            data-testid="digest-enabled-switch"
          />
        </div>

        {schedule.enabled && (
          <div className="space-y-3 mb-5" data-testid="digest-schedule-fields">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Day</Label>
                <Select
                  value={String(schedule.day_of_week)}
                  onValueChange={(v) => setSchedule({ ...schedule, day_of_week: parseInt(v, 10) })}
                >
                  <SelectTrigger className="mt-1.5 rounded-xl" data-testid="digest-day">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS.map((d, i) => (
                      <SelectItem key={d} value={String(i)}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hour (UTC)</Label>
                <Select
                  value={String(schedule.hour)}
                  onValueChange={(v) => setSchedule({ ...schedule, hour: parseInt(v, 10) })}
                >
                  <SelectTrigger className="mt-1.5 rounded-xl" data-testid="digest-hour">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>{i.toString().padStart(2, "0")}:00</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="cc_emails">Also send to <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input
                id="cc_emails"
                value={schedule.cc_emails || ""}
                onChange={(e) => setSchedule({ ...schedule, cc_emails: e.target.value })}
                placeholder="rbt@clinic.com, director@clinic.com"
                className="mt-1.5 rounded-xl"
                data-testid="digest-cc-input"
              />
              <div className="text-[11px] text-slate-500 mt-1.5">Comma-separated emails — RBTs, clinical director, etc. Max 10.</div>
            </div>
          </div>
        )}

        <Button onClick={saveSchedule} disabled={saving.schedule} className="rounded-full" data-testid="save-schedule-button">
          {saving.schedule ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save schedule</>}
        </Button>
      </section>

      {/* Team roster */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="team-roster-section">
        <div className="flex items-center gap-2 mb-4">
          <UsersRound className="w-4 h-4 text-slate-500" />
          <div className="font-display font-bold text-lg">Team roster</div>
        </div>
        <p className="text-sm text-slate-500 mb-5">Invite RBTs and other BCBAs to your workspace. Everyone here shares learners, cards, and reports — and receives the weekly digest.</p>

        {/* Invite code */}
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 mb-5">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2">Team invite code</div>
          {inviteCode ? (
            <div className="flex items-center gap-3">
              <div data-testid="settings-invite-code" className="flex-1 text-center font-display font-extrabold text-2xl tracking-[0.22em] py-3 rounded-lg bg-slate-900 text-white">
                {inviteCode}
              </div>
              <button onClick={copyCode} className="p-3 rounded-lg bg-white border border-slate-200 hover:bg-slate-100" title="Copy" data-testid="settings-copy-invite">
                <Copy className="w-4 h-4" />
              </button>
              <button onClick={rotateCode} disabled={saving.roster} className="p-3 rounded-lg bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-50" title="Regenerate" data-testid="settings-rotate-invite">
                <RefreshCw className={`w-4 h-4 ${saving.roster ? "animate-spin" : ""}`} />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-500">No code yet.</div>
              <Button onClick={rotateCode} disabled={saving.roster} className="rounded-full" data-testid="settings-create-invite">
                {saving.roster ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create invite code"}
              </Button>
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-3">Share with teammates — they enter it on the Team page after signing up.</p>
        </div>

        {/* Members list */}
        <div className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2">Members ({members.length})</div>
        <ul className="divide-y divide-slate-100" data-testid="settings-team-list">
          {members.map((m) => {
            const isMe = m.id === user?.id;
            const isOwner = (m.role_effective || m.team_role) === "owner";
            return (
              <li key={m.id} className="py-3 flex items-center justify-between gap-3" data-testid={`settings-member-${m.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-slate-900 truncate">{m.name}</div>
                    {isMe && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-900 text-white">You</span>}
                    {isOwner && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Owner</span>}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{m.email}</div>
                </div>
                {!isMe && !isOwner && (
                  <>
                    <Select value={m.role_effective || m.team_role || "editor"} onValueChange={(v) => changeRole(m.id, v)}>
                      <SelectTrigger className="w-32 rounded-lg h-9" data-testid={`settings-role-${m.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="editor">Editor</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      onClick={() => removeMember(m.id)}
                      className="p-2 rounded-lg text-rose-600 hover:bg-rose-50"
                      title="Remove"
                      data-testid={`settings-remove-${m.id}`}
                    >
                      <UserMinus className="w-4 h-4" />
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>

        {/* Magic-link email invite */}
        <MagicInviteBlock />
      </section>

      {/* Change password */}
      <ChangePasswordBlock />
    </div>
  );
}

function ChangePasswordBlock() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords don't match"); return; }
    if (next.length < 10) { toast.error("Password must be at least 10 characters"); return; }
    setSaving(true);
    try {
      await http.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Password change failed");
    } finally { setSaving(false); }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="change-password-section">
      <div className="flex items-center gap-2 mb-4">
        <Lock className="w-4 h-4 text-slate-500" />
        <div className="font-display font-bold text-lg">Change password</div>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Rotate your login password. New passwords must be at least 10 characters with mixed case and a digit.
      </p>
      <form onSubmit={submit} className="grid sm:grid-cols-3 gap-3 max-w-2xl">
        <div>
          <Label className="text-xs uppercase tracking-[0.1em] font-bold text-slate-500">Current</Label>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
            required className="mt-1.5 rounded-xl" data-testid="pw-current" autoComplete="current-password" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.1em] font-bold text-slate-500">New</Label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
            required minLength={10} className="mt-1.5 rounded-xl" data-testid="pw-new" autoComplete="new-password" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.1em] font-bold text-slate-500">Confirm</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            required minLength={10} className="mt-1.5 rounded-xl" data-testid="pw-confirm" autoComplete="new-password" />
        </div>
        <div className="sm:col-span-3 flex justify-end">
          <Button type="submit" disabled={saving || !current || !next || !confirm} className="rounded-full" data-testid="pw-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Update password</>}
          </Button>
        </div>
      </form>
    </section>
  );
}

function MagicInviteBlock() {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [sending, setSending] = useState(false);
  const [lastLink, setLastLink] = useState("");

  const send = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      const r = await http.post("/team/magic-invite", { email: email.trim().toLowerCase(), role });
      setLastLink(r.data.join_url || "");
      toast.success(`Invite sent to ${email}`);
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send invite");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="magic-invite-block">
      <div className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2">Invite by email (one-tap)</div>
      <form onSubmit={send} className="flex flex-col sm:flex-row gap-2">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="rbt@clinic.com"
          className="rounded-xl"
          data-testid="magic-invite-email"
          required
        />
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-full sm:w-32 rounded-xl" data-testid="magic-invite-role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="editor">Editor</SelectItem>
            <SelectItem value="viewer">Viewer</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" disabled={sending || !email.trim()} className="rounded-full" data-testid="magic-invite-send">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send link"}
        </Button>
      </form>
      <p className="text-[11px] text-slate-500 mt-2">Emails a one-tap join link valid for 14 days — the recipient skips the code copy-paste.</p>
      {lastLink && (
        <div className="mt-2 text-[11px] text-slate-500 break-all">
          <span className="text-slate-400">Direct link (also emailed):</span>{" "}
          <button
            className="underline hover:text-slate-900"
            data-testid="magic-invite-copy-link"
            onClick={() => { navigator.clipboard?.writeText(lastLink); toast.success("Link copied"); }}
          >{lastLink}</button>
        </div>
      )}
    </div>
  );
}
